import { SlotRendering } from '@frontend/common-types';
import { CommonMessage } from '@frontend/lang';
import { SlotStatus, SlotType } from '@frontend/slot/types';
import React from 'react';
import { createPortal } from 'react-dom';
import { FormattedMessage } from 'react-intl';

import { useSlotController } from './slot.controller';

interface SlotProps<TSlot extends SlotRendering = SlotRendering> {
    slot: TSlot;
    scale: number;
    offsetX: number;
    offsetY: number;
    onSlotClick?: (clickedSlot: TSlot) => void;
    showLabels?: boolean;
    hoverElement?: (slot: TSlot) => React.ReactNode;
    getSlotColor?: (slot: TSlot) => string;
    isDisabled?: boolean;
    renderElement?: (slot: TSlot) => React.ReactNode;
}

export const Slot = <TSlot extends SlotRendering = SlotRendering>({
    slot,
    scale,
    offsetX,
    offsetY,
    onSlotClick,
    showLabels = true,
    hoverElement,
    getSlotColor: getSlotColorOverride,
    isDisabled = false,
    renderElement
}: SlotProps<TSlot>) => {
    const controller = useSlotController(slot, scale, offsetX, offsetY, getSlotColorOverride);

    if (!controller) {
        return null;
    }

    const {
        slotRef,
        isHovered,
        handleMouseEnter,
        handleMouseLeave,
        handleMouseMove,
        colorClass,
        slotLeft,
        slotTop,
        slotWidth,
        slotHeight,
        floatingRefs,
        floatingStyles,
        label
    } = controller;

    const handleClick = () => {
        if (isDisabled) return;
        if (onSlotClick) {
            onSlotClick(slot);
        }
    };

    function render() {
        if (renderElement) {
            return renderElement(slot);
        }
        if (!showLabels) {
            return;
        }
        if (slot.type === SlotType.DROP_STATION && slot.status === SlotStatus.AVAILABLE) {
            return (
                <>
                    <div className='text-background-surface pointer-events-none absolute inset-0 flex items-center justify-center p-1 text-center text-xs leading-tight font-semibold select-none'>
                        {CommonMessage.OBJECTS.SLOT.DROP_OFF_OPENING}
                    </div>
                    <div className='text-background-surface pointer-events-none absolute bottom-0 left-0 pb-1 pl-2 text-xs font-semibold select-none'>
                        {label}
                    </div>
                </>
            );
        }
        return <div className='text-background-surface pointer-events-none absolute bottom-0 left-0 pb-1 pl-2 text-xs font-semibold select-none'>{label}</div>;
    }

    const customHoverElement = hoverElement?.(slot);

    return (
        <>
            <div
                ref={slotRef}
                onClick={handleClick}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onMouseMove={handleMouseMove}
                className={`absolute transition-opacity ${isDisabled ? 'opacity-55' : 'hover:opacity-90'} ${colorClass}`}
                style={{
                    left: `${slotLeft}px`,
                    top: `${slotTop}px`,
                    width: `${slotWidth}px`,
                    height: `${slotHeight}px`,
                    cursor: isDisabled ? 'not-allowed' : onSlotClick ? 'pointer' : 'default'
                }}>
                {render()}
            </div>
            {isHovered &&
                createPortal(
                    <div
                        ref={floatingRefs.setFloating}
                        style={floatingStyles}
                        className='z-50'>
                        <div className='bg-background-surface outline-border-default inline-flex w-fit min-w-50 flex-col items-start justify-start gap-5 rounded-xl p-6 outline-1 outline-offset-[-1px]'>
                            <div className='text-foreground-default justify-start text-lg font-semibold'>
                                {CommonMessage.OBJECTS.SLOT.DEFAULT} {slot.display}
                            </div>
                            <div className='flex flex-col gap-2'>
                                <div className='text-foreground-subtle text-xs'>
                                    {CommonMessage.OBJECTS.DIMENSION.PLURAL}: {slot.dimension?.width} × {slot.dimension?.height}
                                </div>
                                {slot.type && (
                                    <div className='text-foreground-subtle text-xs'>
                                        {CommonMessage.OBJECTS.COMMON_FIELDS.TYPE}: {slot.type}
                                    </div>
                                )}
                                {slot.status && (
                                    <div className='text-foreground-subtle text-xs'>
                                        {CommonMessage.OBJECTS.COMMON_FIELDS.STATUS}: {slot.status}
                                    </div>
                                )}
                                {slot.occupied && (
                                    <div className='text-foreground-subtle text-xs'>
                                        <FormattedMessage
                                            defaultMessage='Occupied'
                                            id='spotRendering.slot.occupied'
                                        />
                                    </div>
                                )}
                            </div>
                            {customHoverElement}
                        </div>
                    </div>,
                    document.body
                )}
        </>
    );
};
